import React from 'react';

type TranslateFn = (key: string) => string;

const SKIP_TAGS = new Set(['script', 'style']);
const TRANSLATABLE_PROP_NAMES = new Set([
  'aria-label',
  'alt',
  'placeholder',
  'title',
]);

function shouldTranslate(text: string): boolean {
  const trimmed = text.trim();

  if (!trimmed) {
    return false;
  }

  if (trimmed.includes('://')) {
    return false;
  }

  if (/^\/[a-z0-9/_-]*$/i.test(trimmed)) {
    return false;
  }

  return /[A-Za-z]/.test(trimmed);
}

function translateText(text: string, t: TranslateFn): string {
  if (!shouldTranslate(text)) {
    return text;
  }

  const leading = text.match(/^\s*/)?.[0] ?? '';
  const trailing = text.match(/\s*$/)?.[0] ?? '';
  const core = text.trim();

  return `${leading}${t(core)}${trailing}`;
}

function translateNode(node: React.ReactNode, t: TranslateFn): React.ReactNode {
  if (typeof node === 'string') {
    return translateText(node, t);
  }

  if (Array.isArray(node)) {
    let changed = false;
    const translated = node.map((item) => {
      const translatedItem = translateNode(item, t);

      if (translatedItem !== item) {
        changed = true;
      }

      return translatedItem;
    });

    return changed ? translated : node;
  }

  if (!React.isValidElement(node)) {
    return node;
  }

  if (typeof node.type === 'string' && SKIP_TAGS.has(node.type)) {
    return node;
  }

  const originalProps = node.props as Record<string, unknown>;
  const nextProps: Record<string, unknown> = {};
  let changed = false;

  for (const propName of TRANSLATABLE_PROP_NAMES) {
    const propValue = originalProps[propName];

    if (typeof propValue !== 'string') {
      continue;
    }

    const translatedValue = translateText(propValue, t);

    if (translatedValue !== propValue) {
      nextProps[propName] = translatedValue;
      changed = true;
    }
  }

  const originalChildren = originalProps.children as React.ReactNode;
  const translatedChildren = translateNode(originalChildren, t);

  if (translatedChildren !== originalChildren) {
    nextProps.children = translatedChildren;
    changed = true;
  }

  if (!changed) {
    return node;
  }

  return React.cloneElement(node, nextProps);
}

export default function translateReactTree<T extends React.ReactNode>(
  node: T,
  t: TranslateFn,
): T {
  return translateNode(node, t) as T;
}
